import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  Avatar,
  useTheme,
  useMediaQuery,
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Switch,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import Brightness4Icon from "@material-ui/icons/Brightness4";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import { toast } from "react-toastify";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIosDevice = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");

const isSafariBrowser = () => {
  const userAgent = window.navigator.userAgent || "";
  return /safari/i.test(userAgent) && !/chrome|crios|android|fxios|edgios/i.test(userAgent);
};

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  appBarHeight: {
    minHeight: 48,
  },
  root: {
    display: "flex",
    height: "100dvh",
  },
  toolbar: {
    minHeight: 48,
    paddingRight: 16,
    paddingLeft: 8,
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    minHeight: "48px",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.background.default,
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
    color: theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: {
      marginRight: 8,
    },
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    color: theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
    },
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      width: "82vw",
      maxWidth: 320,
    },
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: {
    minHeight: 48,
  },
  content: {
    flex: 1,
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  switch: {
    transform: "scale(0.8)",
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
  },
  themeIcon: {
    color: theme.palette.text.primary,
  },
  userButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.primary,
    padding: 6,
    borderRadius: 24,
  },
  userAvatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  userName: {
    fontSize: 14,
    fontWeight: 500,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isTouchDevice = useMediaQuery("(pointer: coarse)");
  const isStandaloneDisplayMode = useMediaQuery("(display-mode: standalone)");
  const [viewportWidth, setViewportWidth] = useState(
    window.innerWidth || 1024
  );
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const { loading: loadingWhatsApps, isSyncing, syncInfo } =
    useContext(WhatsAppsContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const isMobileUserAgent = /android|iphone|ipad|ipod|mobile/i.test(
    window.navigator.userAgent || ""
  );
  const isStandaloneApp = isStandaloneDisplayMode || isStandalone();
  const isMobileLayout =
    isSmallScreen ||
    (isTouchDevice &&
      (viewportWidth <= 1024 || (isStandaloneApp && isMobileUserAgent)));

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth || 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setDrawerVariant(isMobileLayout ? "temporary" : "permanent");
    setDrawerOpen(!isMobileLayout);
  }, [isMobileLayout]);

  useEffect(() => {
    const handleBeforeInstallPrompt = event => {
      event.preventDefault();
      setDeferredInstallPrompt(event);
    };

    const handleAppInstalled = () => {
      setDeferredInstallPrompt(null);
      toast.success(i18n.t("mainDrawer.appBar.user.installSuccess"));
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleInstallApp = async () => {
    handleCloseMenu();

    if (isStandalone()) {
      toast.info(i18n.t("mainDrawer.appBar.user.installAlready"));
      return;
    }

    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      try {
        await deferredInstallPrompt.userChoice;
      } finally {
        setDeferredInstallPrompt(null);
      }
      return;
    }

    if (isIosDevice() && isSafariBrowser()) {
      toast.info(i18n.t("mainDrawer.appBar.user.installIos"), {
        autoClose: 9000
      });
      return;
    }

    toast.info(i18n.t("mainDrawer.appBar.user.installFallback"), {
      autoClose: 9000
    });
  };

  const drawerClose = () => {
    if (isMobileLayout) {
      setDrawerOpen(false);
    }
  };

  if (loading || loadingWhatsApps) {
    return <BackdropLoading />;
  }

  const syncDetails = syncInfo
    ? `${syncInfo.message}${
        syncInfo.total > 0
          ? ` (${syncInfo.processed}/${syncInfo.total})`
          : ""
      } - mensagens: ${syncInfo.importedMessages} - contatos: ${
        syncInfo.importedContacts
      }`
    : "Sincronizando dados da conexao";

  return (
    <div className={classes.root}>
      {isSyncing && (
        <BackdropLoading
          message={`Sincronizando ${syncInfo?.sessionName || "conexao"}`}
          details={syncDetails}
          progress={syncInfo?.percentage}
        />
      )}
      <Drawer
        variant={drawerVariant}
        ModalProps={{ keepMounted: true }}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List>
          <MainListItems drawerClose={drawerClose} />
        </List>
        <Divider />
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(
          classes.appBar,
          !isMobileLayout && drawerOpen && classes.appBarShift
        )}
      >
        <Toolbar variant="dense" className={clsx(classes.toolbar, classes.appBarHeight)}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            noWrap
            className={classes.title}
          >
            Vip Turismo WhaTicket
          </Typography>

          <div className={classes.themeSwitchContainer}>
            <Brightness4Icon className={classes.themeIcon} />
            <Switch
              checked={darkMode}
              onChange={toggleTheme}
              color="default"
              className={classes.switch}
            />
          </div>

          {user.id && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={clsx(classes.iconButton, classes.userButton)}
            >
              <Avatar src={user.profileImageUrl} className={classes.userAvatar}>
                {(user.firstName || user.name || "U").charAt(0)}
              </Avatar>
              <span className={classes.userName}>
                {user.firstName || user.name || "Usuario"}
              </span>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleInstallApp}>
                {i18n.t("mainDrawer.appBar.user.install")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
